/**
 * Live Agent view — roster + protocol enum + origin-registry cross-reference.
 *
 * Per ADR-0159 §3 / §4 / §6 the roster is extracted out of the monolithic
 * `Simulation.tsx` into this dedicated module. Two data sets sit here:
 *
 *   1. `AGENTS` — the client-side picker roster, one entry per agent the
 *      merchant might reasonably want to demo. Each entry carries a
 *      `protocol` field that selects the journey builder at run time
 *      (`journeys/index.ts` exhaustive-switch dispatcher).
 *
 *   2. `ORIGIN_AGENT_ID` — the roster-id → origin `agent_id[]` map that
 *      backs the `useAgentRegistry` tier resolution (§4). A single roster
 *      entry may resolve to several origin identities (Claude → ClaudeBot,
 *      Claude-User, Claude-SearchBot). The map values MUST match the
 *      forward migration's placeholder scheme (see ADR §3 "Per-identity
 *      rows"); a golden test pins them to the fixture of `GET /v1/agents`.
 *
 * The picker/roster deliberately does NOT include the social link-preview
 * bots or the `agent_sill_test_fixture` — those are filtered by absence
 * here (ADR §4 "Social link-preview bots are never in the roster; client-
 * side filter is inherent").
 *
 * The documented `userAgent` strings match the vendors' published bot docs
 * as of the 2026-07-04 ADR-0159 research pass; the honest-caption discipline
 * (browser cannot set `User-Agent` from the dashboard's own fetch) stays as
 * documented at the field.
 */

import type { AgentId } from '@sill/types';

/**
 * Per-protocol journey shape — the enum keyed by the dispatcher in
 * `journeys/index.ts`. Exhaustive-switched so a new addition typechecks at
 * every builder. Mapping (per ADR-0159 §3):
 *   `mcp`             — Claude
 *   `acp`             — ChatGPT
 *   `a2a-ap2`         — Gemini
 *   `transact-pilot`  — Perplexity, Copilot
 *   `discovery-only`  — Meta, Mistral, Apple, DuckDuckGo
 *   `unidentifiable`  — Grok
 *   `blocked`         — Unverified
 *   `custom`          — self-built
 */
export type Protocol =
  | 'mcp'
  | 'acp'
  | 'a2a-ap2'
  | 'transact-pilot'
  | 'discovery-only'
  | 'unidentifiable'
  | 'blocked'
  | 'custom';

/**
 * Registry-backed tier (ADR-0159 §4). Never defaults to `'verified'` — a
 * failed / pending fetch resolves to `'unknown'` so the signature preview
 * gate falls back to the honest "NO KEY" state.
 */
export type VerificationTier = 'verified' | 'identified' | 'unknown';

export interface AgentDef {
  id: string;
  name: string;
  org: string;
  color: string;
  role: string;
  /**
   * Selects the journey builder (§3). One protocol per roster entry;
   * a single agent picker entry may still map to multiple origin identities
   * via {@link ORIGIN_AGENT_ID}.
   */
  protocol: Protocol;
  /**
   * The documented User-Agent header this agent sends in the real world.
   * Sources: each vendor's published bot/UA docs (OpenAI, Anthropic,
   * Perplexity, Meta, Mistral) + observed traffic (Gemini, Grok, Copilot —
   * plain browser UAs with no distinct bot token).
   *
   * IMPORTANT (honesty constraint): the dashboard's own `fetch()` cannot
   * set this header — browsers forbid it. We surface it as "what {agent}
   * sends in the real world," NOT as the header our LIVE fetch literally
   * sent. The response is still genuinely live; the UA is documented.
   */
  userAgent: string;
  /**
   * `true` — the agent self-identifies as a bot (the UA contains a
   * distinct token like `ChatGPT-User`, `Claude-User`, `meta-externalagent`).
   * `false` — the agent masquerades as a browser; the UA is generic Chrome
   * / Safari / Edge with no identifying token. Drives the masquerade note
   * and the identity-phase payoff line ("UA is useless for identity —
   * only the signed mandate proves who it is").
   */
  declared: boolean;
  /**
   * Whether the documented bot honors `robots.txt`. Surfaced as a small
   * caption in the discovery phase.
   */
  honorsRobots: boolean;
  /**
   * ADR-0161 — the `agent_id` values the EDGE writes into
   * `discovery_context.identification_match.agent_id` on a matched
   * visit (see `apps/edge/src/ingest/handler.ts:276-281` +
   * `apps/edge/src/identity/registry.ts::BUNDLED_REGISTRY`). This is
   * the join key for the observed-traffic panel.
   *
   * DISTINCT from `ORIGIN_AGENT_ID[rosterId]` — that map holds the
   * origin `agent` table's Crockford-ULID primary keys; those keys
   * are NOT what the edge writes into audit rows. Both maps exist
   * because the two sides deliberately drifted (origin = source of
   * truth for KV seed; edge = additive-only bundled floor). See
   * ADR-0060 + ADR-0159 for the drift rationale.
   *
   * Empty for `grok` (no bundled edge identity), `custom`, and
   * `unverified` — the observed-traffic panel does NOT fetch in
   * those cases.
   */
  readonly edgeAgentIds: readonly string[];
}

/**
 * Roster → origin `agent_id[]` map. The values MUST match the parity
 * migration (`services/api/migrations/20260704000010_agent_registry_parity.sql`)
 * — a golden test in the test file asserts every entry appears in the
 * `/v1/agents` fixture (ADR-0159 §Test plan). When the migration adds a
 * row, this map MUST be updated in the same slice.
 *
 * A roster entry with an empty array (`custom`, `unverified`) means "no
 * origin identity to resolve" — the tier is always `'unknown'` (custom is
 * a merchant-supplied endpoint; unverified is the honest "no signed
 * identity" state, gated to `blocked` by the picker group).
 */
export const ORIGIN_AGENT_ID: Readonly<Record<string, readonly AgentId[]>> = {
  chatgpt: [
    'agent_01HXAGT0CGPTBRWS0000000000' as AgentId, // edge: agent_openai_chatgpt_user
    'agent_01HXAGT0CGPTMBL00000000000' as AgentId, // edge: agent_openai_chatgpt
    'agent_01HXAGT0PENA00000000000000' as AgentId, // edge: agent_openai_gpt
    'agent_01HXAGT0CSRCHBT00000000000' as AgentId, // edge: agent_openai_searchbot
    'agent_01HXAGT0CADSBT000000000000' as AgentId, // edge: agent_openai_adsbot
  ],
  claude: [
    'agent_01HXAGT0CLDBRWS00000000000' as AgentId, // edge: agent_anthropic_claude_user
    'agent_01HXAGT0ANTHRPC00000000000' as AgentId, // edge: agent_anthropic_claude
    'agent_01HXAGT0CLDSRCH00000000000' as AgentId, // edge: agent_anthropic_claude_searchbot
  ],
  perplexity: [
    'agent_01HXAGT0PRPLXBT00000000000' as AgentId, // edge: agent_perplexity
    'agent_01HXAGT0PRPLXBR00000000000' as AgentId, // edge: agent_perplexity_user
  ],
  gemini: ['agent_01HXAGT0GGLE00000000000000' as AgentId], // edge: agent_google_gemini
  grok: [],
  copilot: ['agent_01HXAGT0BNGBT0000000000000' as AgentId], // edge: agent_microsoft_bingbot
  meta: [
    'agent_01HXAGT0METAEXAG0000000000' as AgentId, // edge: agent_meta_externalagent
    'agent_01HXAGT0METAEXFT0000000000' as AgentId, // edge: agent_meta_externalfetcher
    'agent_01HXAGT0METAWEBX0000000000' as AgentId, // edge: agent_meta_webindexer
    'agent_01HXAGT0METAEXAD0000000000' as AgentId, // edge: agent_meta_externalads
  ],
  mistral: ['agent_01HXAGT0MSTRL0000000000000' as AgentId], // edge: agent_mistral_user
  apple: ['agent_01HXAGT0APPLXTND0000000000' as AgentId], // edge: agent_apple_extended
  duckduckgo: ['agent_01HXAGT0DCKASST00000000000' as AgentId], // edge: agent_duckduckgo_assistbot
  custom: [],
  unverified: [],
};

/**
 * Picker roster. Order = display order in the dropdown. Grouping
 * (`VERIFIED` / `IDENTIFIED` / `UNVERIFIED · BLOCKED PATH`) is driven by
 * the tier resolved at render time, not by an inline `verified` boolean
 * (ADR-0159 §4 — the honest tri-state posture supersedes the earlier
 * client-side `verified: true` fib).
 */
export const AGENTS: readonly AgentDef[] = [
  {
    id: 'chatgpt',
    name: 'ChatGPT',
    org: 'OpenAI',
    color: '#10A37F',
    role: 'Agent',
    protocol: 'acp',
    // OpenAI's documented ChatGPT-User bot UA (docs.openai.com/bots).
    // Verified 2026-07-04 (ADR-0159 research pass).
    userAgent:
      'Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko; compatible; ChatGPT-User/1.0; +https://openai.com/bot)',
    declared: true,
    honorsRobots: true,
    edgeAgentIds: [
      'agent_openai_chatgpt_user',
      'agent_openai_chatgpt',
      'agent_openai_gpt',
      'agent_openai_searchbot',
      'agent_openai_adsbot',
    ],
  },
  {
    id: 'claude',
    name: 'Claude',
    org: 'Anthropic',
    color: '#CC785C',
    role: 'Assistant',
    protocol: 'mcp',
    // Anthropic's documented Claude-User bot UA
    // (docs.claude.com/en/docs/agents/web-crawlers). Verified 2026-07-04.
    userAgent:
      'Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko; compatible; Claude-User/1.0; +https://claude.com/crawling/bots.json)',
    declared: true,
    honorsRobots: true,
    edgeAgentIds: [
      'agent_anthropic_claude_user',
      'agent_anthropic_claude',
      'agent_anthropic_claude_searchbot',
    ],
  },
  {
    id: 'perplexity',
    name: 'Perplexity',
    org: 'Perplexity AI',
    color: '#20B8CD',
    role: 'Answer engine',
    protocol: 'transact-pilot',
    // Perplexity-User is Perplexity's user-initiated fetch UA. Vendor
    // docs (docs.perplexity.ai) note `robots.txt` is "generally ignored"
    // by Perplexity-User (the surfaced teaching note in the journey).
    userAgent:
      'Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko; compatible; Perplexity-User/1.0; +https://perplexity.ai/perplexity-user)',
    declared: true,
    // Perplexity-User specifically does NOT honor robots.txt per vendor
    // docs (PerplexityBot the training crawler does). The picker entry
    // surfaces the user-fetch UA; the discovery-phase teaching note is
    // grade-A on the "does not honor" side.
    honorsRobots: false,
    edgeAgentIds: ['agent_perplexity', 'agent_perplexity_user'],
  },
  {
    id: 'gemini',
    name: 'Gemini',
    org: 'Google',
    color: '#4285F4',
    role: 'Assistant',
    protocol: 'a2a-ap2',
    userAgent:
      'Mozilla/5.0 (Linux; Android 10; K) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/139.0.0.0 Mobile Safari/537.36',
    declared: false,
    honorsRobots: false,
    edgeAgentIds: ['agent_google_gemini'],
  },
  {
    id: 'grok',
    name: 'Grok',
    org: 'xAI',
    color: '#4B5563',
    role: 'Real-time agent',
    protocol: 'unidentifiable',
    userAgent:
      'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/17.4 Safari/605.1.15',
    declared: false,
    honorsRobots: false,
    // No bundled edge identity — the OBSERVED panel never fetches.
    edgeAgentIds: [],
  },
  {
    id: 'copilot',
    name: 'Copilot',
    org: 'Microsoft',
    color: '#0078D4',
    role: 'Work agent',
    protocol: 'transact-pilot',
    // Copilot Actions browsing uses a stock Edge/Chromium UA (observed).
    // Bingbot is a separate crawler feeding Copilot search grounding.
    userAgent:
      'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/139.0.0.0 Safari/537.36 Edg/139.0.0.0',
    declared: false,
    honorsRobots: false,
    edgeAgentIds: ['agent_microsoft_bingbot'],
  },
  {
    id: 'meta',
    name: 'Meta AI',
    org: 'Meta',
    color: '#0866FF',
    role: 'Assistant',
    protocol: 'discovery-only',
    userAgent:
      'meta-externalagent/1.1 (+https://developers.facebook.com/docs/sharing/webmasters/crawler)',
    declared: true,
    honorsRobots: true,
    edgeAgentIds: [
      'agent_meta_externalagent',
      'agent_meta_externalfetcher',
      'agent_meta_webindexer',
      'agent_meta_externalads',
    ],
  },
  {
    id: 'mistral',
    name: 'Le Chat',
    org: 'Mistral',
    color: '#FA520F',
    role: 'Assistant',
    protocol: 'discovery-only',
    userAgent:
      'Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko; compatible; MistralAI-User/1.0; +https://docs.mistral.ai/robots)',
    declared: true,
    honorsRobots: true,
    edgeAgentIds: ['agent_mistral_user'],
  },
  // Apple + DuckDuckGo added per ADR-0159 Open Questions "Recommend
  // adding both — the widened vendor set was chosen to admit them."
  {
    id: 'apple',
    name: 'Apple Intelligence',
    org: 'Apple',
    color: '#000000',
    role: 'Assistant',
    protocol: 'discovery-only',
    userAgent:
      'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_5) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/13.1.1 Safari/605.1.15 (Applebot-Extended/0.1; +http://www.apple.com/go/applebot)',
    declared: true,
    honorsRobots: true,
    edgeAgentIds: ['agent_apple_extended'],
  },
  {
    id: 'duckduckgo',
    name: 'DuckAssist',
    org: 'DuckDuckGo',
    color: '#DE5833',
    role: 'Answer engine',
    protocol: 'discovery-only',
    userAgent: 'DuckAssistBot/1.2; (+http://duckduckgo.com/duckassistbot.html)',
    declared: true,
    honorsRobots: true,
    edgeAgentIds: ['agent_duckduckgo_assistbot'],
  },
  {
    id: 'custom',
    name: 'Custom Agent',
    org: 'Self-built',
    color: '#867E72',
    role: 'Headless',
    protocol: 'custom',
    userAgent: 'YourBrand-Agent/1.0',
    declared: true,
    honorsRobots: true,
    // Merchant-supplied endpoint — no bundled edge identity.
    edgeAgentIds: [],
  },
  {
    id: 'unverified',
    name: 'Unverified',
    org: 'Unknown · no signature',
    color: '#FF6B47',
    role: 'Blocked path',
    protocol: 'blocked',
    userAgent: 'Mozilla/5.0 (compatible)',
    declared: false,
    honorsRobots: false,
    // No signed identity by construction — no edge id, no observed traffic.
    edgeAgentIds: [],
  },
];

export function findAgent(agentId: string): AgentDef {
  return AGENTS.find((a) => a.id === agentId) ?? AGENTS[0]!;
}
